import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export const FadeUp = ({ children, delay = 0, className = "", y = 36 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const MaskedLines = ({ lines, className = "", lineClassName = "", delay = 0 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-1">
        <motion.span
          className={`block ${lineClassName}`}
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 1, delay: delay + i * 0.14, ease: EASE }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

export const Chapter = ({ number, eyebrow, title, className = "" }) => (
  <FadeUp className={className}>
    <div className="flex items-center gap-4 mb-6">
      <span className="font-mono text-xs text-gold tracking-[0.3em]">{number}</span>
      <span className="h-px w-12 bg-gold/40" />
      <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">{eyebrow}</span>
    </div>
    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory tracking-tight font-light leading-[1.1]">
      {title}
    </h2>
  </FadeUp>
);

export const GoldLink = ({ to, children, testId, className = "", onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testId}
    className={`group inline-flex items-center gap-3 bg-gold text-ink px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium transition-colors duration-500 hover:bg-gold-glow ${className}`}
  >
    {children}
    <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
  </Link>
);

export const GhostLink = ({ to, children, testId, className = "" }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`group inline-flex items-center gap-3 border border-gold/40 text-ivory px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium transition-colors duration-500 hover:border-gold hover:text-gold ${className}`}
  >
    {children}
    <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
  </Link>
);

export const PageHero = ({ eyebrow, title, sub, image }) => (
  <section className="relative pt-40 pb-20 lg:pt-52 lg:pb-28 overflow-hidden">
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
      </div>
    )}
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeUp>
        <div className="flex items-center gap-4 mb-6">
          <span className="h-px w-12 bg-gold/40" />
          <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">{eyebrow}</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory font-light tracking-tight leading-[1.05] max-w-3xl">
          {title}
        </h1>
        {sub && <p className="mt-6 text-stone-mist text-base sm:text-lg font-light max-w-2xl leading-relaxed">{sub}</p>}
      </FadeUp>
    </div>
  </section>
);
